import {startTransition} from 'react'

const isBrowser = typeof globalThis !== 'undefined' && typeof (globalThis as {window?: unknown}).window !== 'undefined'

const isTestEnvironment = typeof process !== 'undefined' && process.env.NODE_ENV === 'test'

type WindowWithRequestIdleCallback = typeof globalThis & {
  requestIdleCallback?: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number
}

type ScheduleOptions = {
  timeout?: number
  useIdleCallback?: boolean
}

// Fallback timeout: how long we are willing to defer the update before forcing it to run
const DEFAULT_OPTIONS: Required<ScheduleOptions> = {
  timeout: 150,
  useIdleCallback: true,
}

export function scheduleLowPriorityUpdate(update: () => void, options: ScheduleOptions = {}) {
  const {timeout, useIdleCallback} = {...DEFAULT_OPTIONS, ...options}

  // Outside the browser, or when running unit tests skip the deferral entirely
  if (!isBrowser || isTestEnvironment) {
    update()
    return
  }

  // Mark the work as low priority via startTransition if it is available, so user
  // interactions can pre-empt the update if needed
  const run = () => {
    if (typeof startTransition === 'function') {
      startTransition(update)
    } else {
      update()
    }
  }

  // Defer execution until the browser goes idle when possible. This keeps the main thread clear
  // during typing, scrolling, and other high-priority interactions
  const browserGlobal = globalThis as WindowWithRequestIdleCallback
  if (useIdleCallback && typeof browserGlobal.requestIdleCallback === 'function') {
    browserGlobal.requestIdleCallback(run, {timeout})
    return
  }

  // As a fallback (older browsers, SSR), use a macrotask to yield control so the current call stack
  // can unwind before we re-attempt the work
  setTimeout(run, 0)
}
